/**
 * Cycle types.
 * 
 * Legacy DEGAP-oriented symbols remain for compatibility, but callers should
 * prefer the neutral cycle-first aliases added in this module.
 * 
 * A Cycle is a time-bound container that exposes authoritative cycle-state
 * markers:
 * DECIDE → ENGAGE → GATHER → ARRANGE → PRIORITIZE
 * 
 * Each phase has:
 * - Required inputs (must be present to enter)
 * - Outputs (produced during the phase)
 * - Allowed operations
 * - Forbidden operations
 * - Invariants (conditions that must always hold)
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS (Doc 02, Doc 03)
 * ============================================================================
 * 
 * INVARIANT 1: Cycle state is authoritative for DEGAP phase.
 *   - Only the Cycle state machine determines current phase
 *   - No other entity (Session, Task, Catchball) can advance or set phase
 *   - Phase transitions require explicit gate validation
 * 
 * INVARIANT 2: Catchball loops do not advance cycle phases.
 *   - Catchball acceptance (Accept Provisional) does NOT trigger phase advance
 *   - Catchball is a coordination mechanism, not a phase controller
 *   - Catchball may span multiple cycles as a parent container
 * 
 * INVARIANT 3: Governance state overlays but does not block core transitions.
 *   - Governance (Proposed → Decided → Ratified → Published) is orthogonal
 *   - Cycle can advance even if governance is pending
 *   - Governance affects visibility/authority, not phase progression
 */

import type { CycleTemplateSeed } from './cycleTemplate';

// CyclePrerequisite type deprecated - now part of unified task model with TaskCategory

// ============================================================================
// Phase Definition
// ============================================================================

export type DegapPhase = 'DECIDE' | 'ENGAGE' | 'GATHER' | 'ARRANGE' | 'PRIORITIZE';
export type CyclePhase = DegapPhase;

export const PHASE_ORDER: DegapPhase[] = ['DECIDE', 'ENGAGE', 'GATHER', 'ARRANGE', 'PRIORITIZE'];
export const CYCLE_PHASE_ORDER: CyclePhase[] = PHASE_ORDER;

// ============================================================================
// Cycle Mode (Episodic, Periodic, Continuous)
// ============================================================================

/**
 * CycleMode determines how the prioritization cycle operates:
 * - episodic: One-time prioritization event (e.g., annual planning, project kickoff)
 * - periodic: Scheduled recurring prioritization (e.g., quarterly review, monthly sprint planning)
 * - continuous: Always-open cycle for ongoing prioritization (e.g., continuous backlog refinement)
 */
export type CycleMode = 'episodic' | 'periodic' | 'continuous';

/**
 * Frequency options for periodic cycles
 */
export type CycleFrequency = 'weekly' | 'monthly' | 'quarterly' | 'semi-annually' | 'annually';

/**
 * CycleCadence defines the timing and recurrence of a prioritization cycle
 */
export interface CycleCadence {
  mode: CycleMode;
  
  // For periodic mode: schedule information
  frequency?: CycleFrequency;
  nextScheduledStart?: Date;
  lastCompletedAt?: Date;
  
  // For continuous mode: cycle stays open indefinitely
  indefinite?: boolean;
  
  // For episodic mode: target completion
  targetEndDate?: Date;
}

/**
 * Default cadence for new cycles
 */
export const DEFAULT_CYCLE_CADENCE: CycleCadence = {
  mode: 'episodic',
};

/**
 * Human-readable descriptions for cycle modes
 */
export const CYCLE_MODE_DESCRIPTIONS: Record<CycleMode, { label: string; description: string }> = {
  episodic: {
    label: 'One-Time',
    description: 'A single prioritization event, such as annual planning or a project kickoff.',
  },
  periodic: {
    label: 'Scheduled',
    description: 'Recurring prioritization on a regular schedule (weekly, monthly, quarterly, etc.).',
  },
  continuous: {
    label: 'Always Open',
    description: 'An ongoing cycle that stays open indefinitely for continuous prioritization.',
  },
};

export interface PhaseDefinition {
  name: DegapPhase;
  purpose: string;
  activatedSpaces: number[];
  allowedOperations: string[];
  forbiddenOperations: string[];
}

export const PHASE_DEFINITIONS: Record<DegapPhase, PhaseDefinition> = {
  DECIDE: {
    name: 'DECIDE',
    purpose: 'Clarify outcomes, boundaries, and what "success" means',
    activatedSpaces: [1, 2, 11], // Intake, Shaping, Governance
    allowedOperations: [
      'intake_items',
      'define_outcomes',
      'define_boundaries',
      'assign_roles',
      'set_success_criteria',
    ],
    forbiddenOperations: [
      'run_tools',
      'create_focus_lists',
      'publish_priorities',
    ],
  },
  ENGAGE: {
    name: 'ENGAGE',
    purpose: 'Align participants, roles, and decision rights',
    activatedSpaces: [7, 11], // Session Support, Governance
    allowedOperations: [
      'create_sessions',
      'onboard_participants',
      'assign_decision_rights',
      'schedule_sessions',
    ],
    forbiddenOperations: [
      'shape_items',
      'run_tools',
    ],
  },
  GATHER: {
    name: 'GATHER',
    purpose: 'Collect, shape, and structure candidate work items',
    activatedSpaces: [1, 2, 3], // Intake, Shaping, Attribute Management
    allowedOperations: [
      'intake_items',
      'promote_to_items',
      'edit_attributes',
      'deduplicate_items',
      'merge_items',
      'version_items',
    ],
    forbiddenOperations: [
      'select_criteria',
      'configure_tools',
      'run_tools',
    ],
  },
  ARRANGE: {
    name: 'ARRANGE',
    purpose: 'Apply tools, criteria, and scoring to derive structured priorities',
    activatedSpaces: [4, 5, 6, 7], // Criteria, Focus List Creation, Editing, Session
    allowedOperations: [
      'select_tools',
      'configure_tools',
      'run_tools',
      'create_lenses',
      'select_criteria',
      'create_snapshots',
      'compare_snapshots',
      'run_live_sessions',
    ],
    forbiddenOperations: [
      'publish_priorities',
      'execute_items',
      'edit_outcome_drivers',
    ],
  },
  PRIORITIZE: {
    name: 'PRIORITIZE',
    purpose: 'Commit to a final ordering, publish it, and manage drift',
    activatedSpaces: [6, 8, 9, 10, 12], // Editing, Drift, Realignment, Focus Map, Analytics
    allowedOperations: [
      'manual_reorder',
      'set_cut_line',
      'accept_tool_outputs',
      'reject_tool_outputs',
      'publish_snapshot',
      'monitor_drift',
      'realign_priorities',
      'explain_priorities',
    ],
    forbiddenOperations: [
      'edit_item_attributes',
      'edit_criteria_on_published',
    ],
  },
};

export type CyclePhaseDefinition = PhaseDefinition;
export const CYCLE_PHASE_DEFINITIONS: Record<CyclePhase, CyclePhaseDefinition> = PHASE_DEFINITIONS;

// ============================================================================
// Phase Outputs - What each phase produces
// ============================================================================

export interface DecidePhaseOutputs {
  clarityDocument?: string;          // What this prioritization effort is for
  outcomeDrivers: string[];          // Key outcomes we're optimizing for
  domainBoundaries?: string;         // What's in/out of scope
  successCriteria?: string;          // Definition of "priority worthiness"
  roleAssignments: CycleRoleAssignment[];
}

export interface EngagePhaseOutputs {
  participants: CycleParticipant[];
  scheduledSessions: string[];       // Session IDs
  decisionRights?: string;           // Who decides what
  workingAgreements?: string;        // How we'll work together
}

export interface GatherPhaseOutputs {
  shapedItemIds: string[];           // Items ready for tools
  attributeSetId?: string;           // Attribute configuration used
  itemCount: number;
  readyForArrangeCount: number;
}

export interface ArrangePhaseOutputs {
  itemPoolIds: string[];            // Item pools created
  toolRuns: ToolRunRecord[];         // History of tool executions
  snapshotIds: string[];             // Snapshots created
  criteriaSetId?: string;            // Criteria used
}

export interface PrioritizePhaseOutputs {
  publishedSnapshotId?: string;      // The authoritative snapshot
  publishedAt?: Date;
  cutLinePosition?: number;
  driftEvents: DriftEventRecord[];
  realignmentHistory: RealignmentRecord[];
}

export interface CycleOutputs {
  decide: DecidePhaseOutputs;
  engage: EngagePhaseOutputs;
  gather: GatherPhaseOutputs;
  arrange: ArrangePhaseOutputs;
  prioritize: PrioritizePhaseOutputs;
}

// ============================================================================
// Supporting Types
// ============================================================================

export interface CycleRoleAssignment {
  userId: string;
  role: string;
  assignedAt: Date;
  assignedBy: string;
  scope?: string;
}

export interface CycleParticipant {
  userId: string;
  name: string;
  email?: string;
  role: string;
  joinedAt?: Date;
  status: 'invited' | 'active' | 'inactive';
  isGroup?: boolean;
}

export interface ToolRunRecord {
  id: string;
  toolId: string;
  toolName: string;
  runAt: Date;
  runBy: string;
  itemCount: number;
  snapshotId?: string;
  itemPoolId?: string;
  status: 'completed' | 'failed' | 'cancelled';
}

export interface DriftEventRecord {
  id: string;
  detectedAt: Date;
  type: 'item_below_line_started' | 'item_above_line_not_started' | 'new_high_value_item' | 'context_change';
  description: string;
  itemId?: string;
  resolved: boolean;
  resolvedAt?: Date;
  resolution?: 'accepted' | 'rejected';
}

export interface RealignmentRecord {
  id: string;
  occurredAt: Date;
  reason: string;
  previousSnapshotId: string;
  newSnapshotId: string;
  changedBy: string;
}

// ============================================================================
// Transition Requirements
// ============================================================================

export interface PhaseTransitionRequirement {
  from: DegapPhase;
  to: DegapPhase;
  requirements: string[];
  validate: (outputs: CycleOutputs) => { valid: boolean; missing: string[] };
}

export const PHASE_TRANSITIONS: PhaseTransitionRequirement[] = [
  {
    from: 'DECIDE',
    to: 'ENGAGE',
    requirements: [
      'Outcome drivers documented (optional - imported from strategy if available)',
      'Cycle owner identified',
    ],
    validate: (_outputs) => {
      // Outcome drivers are now optional - they can be imported from the linked strategy
      // or added manually, but are not required to advance.
      //
      // Role assignments are also optional here because the cycle owner (ownerId)
      // is set during cycle creation and satisfies the ownership requirement.
      // The store-level validation checks for ownerId.
      const missing: string[] = [];
      // No hard requirements - just recommendations
      // The cycle owner is checked at the store level via ownerId
      return { valid: true, missing };
    },
  },
  {
    from: 'ENGAGE',
    to: 'GATHER',
    requirements: [
      'Participant list finalized',
      'Decision rights assigned',
      'Session modes selected',
    ],
    validate: (outputs) => {
      const missing: string[] = [];
      if (!outputs?.engage?.participants?.length) {
        missing.push('No participants added');
      }
      return { valid: missing.length === 0, missing };
    },
  },
  {
    from: 'GATHER',
    to: 'ARRANGE',
    requirements: [
      'Items satisfy minimum structural requirements',
      'Attribute sets meet comparison requirements',
      'Criteria Authority confirms readiness',
    ],
    validate: (outputs) => {
      const missing: string[] = [];
      if (!outputs?.gather?.readyForArrangeCount) {
        missing.push('No items ready for arrangement');
      }
      return { valid: missing.length === 0, missing };
    },
  },
  {
    from: 'ARRANGE',
    to: 'PRIORITIZE',
    requirements: [
      'At least one PrioritySnapshot exists',
      'Item Pool owner selects or accepts a snapshot',
    ],
    validate: (outputs) => {
      const missing: string[] = [];
      if (!outputs?.arrange?.snapshotIds?.length) {
        missing.push('No snapshots created - run at least one tool');
      }
      if (!outputs?.arrange?.itemPoolIds?.length) {
        missing.push('No item pool created');
      }
      return { valid: missing.length === 0, missing };
    },
  },
];

// ============================================================================
// Phase Status (Core B - Parallel Phase Support)
// ============================================================================

/**
 * Status of a single phase within a cycle
 * Supports parallel execution where multiple phases can be active simultaneously
 */
export type PhaseProgressStatus = 'not_started' | 'in_progress' | 'complete';

/**
 * Detailed status tracking for each phase
 */
export interface PhaseStatus {
  phase: DegapPhase;
  status: PhaseProgressStatus;
  progress: number;  // 0-100 percentage
  startedAt?: Date;
  completedAt?: Date;
  startedBy?: string;
  completedBy?: string;
  notes?: string;
}

export type CyclePhaseStatus = PhaseStatus;

/**
 * Create initial phase statuses for a new cycle
 */
export function createInitialPhaseStatuses(): Record<DegapPhase, PhaseStatus> {
  return {
    DECIDE: { phase: 'DECIDE', status: 'not_started', progress: 0 },
    ENGAGE: { phase: 'ENGAGE', status: 'not_started', progress: 0 },
    GATHER: { phase: 'GATHER', status: 'not_started', progress: 0 },
    ARRANGE: { phase: 'ARRANGE', status: 'not_started', progress: 0 },
    PRIORITIZE: { phase: 'PRIORITIZE', status: 'not_started', progress: 0 },
  };
}

// ============================================================================
// Phase Parallelism Rules (Core B)
// ============================================================================

/**
 * Defines which phases can run in parallel with each other.
 * Key: current phase
 * Value: phases that can run concurrently with this phase
 * 
 * Rules:
 * - DECIDE must complete before any other phase starts
 * - ENGAGE and GATHER can run in parallel
 * - GATHER and ARRANGE can overlap
 * - PRIORITIZE requires ARRANGE to be complete
 */
export const PHASE_PARALLELISM: Record<DegapPhase, DegapPhase[]> = {
  DECIDE: [],                      // Must complete alone first
  ENGAGE: ['GATHER'],              // Can run with GATHER
  GATHER: ['ENGAGE', 'ARRANGE'],   // Can overlap both directions
  ARRANGE: ['GATHER'],             // Can start while GATHER finishes
  PRIORITIZE: [],                  // Must have ARRANGE complete
};

/**
 * Gate state from cycle_progress view or readiness evaluator.
 * Used to determine if a phase can be started (invariant-based, not phase-status).
 */
export interface CycleGateState {
  canEnterEngage: boolean;
  canEnterGather: boolean;
  canEnterArrange: boolean;
  canEnterPrioritize: boolean;
  canComplete?: boolean;
}

export type CycleReadinessGateState = CycleGateState;

/**
 * Check if a phase can be started using invariant-based gate state.
 * Uses can_enter_* flags from cycle_progress (pool/criteria locks, participants, etc.).
 * If gateState is missing, returns allowed: false (do not silently allow).
 */
export function canStartPhase(
  targetPhase: DegapPhase,
  gateState: CycleGateState | null | undefined
): { allowed: boolean; reason?: string } {
  if (gateState == null) {
    return { allowed: false, reason: 'Gate state unavailable' };
  }
  switch (targetPhase) {
    case 'DECIDE':
      return { allowed: true };
    case 'ENGAGE':
      return gateState.canEnterEngage
        ? { allowed: true }
        : { allowed: false, reason: 'Gates for ENGAGE not satisfied (e.g. cycle owner required)' };
    case 'GATHER':
      return gateState.canEnterGather
        ? { allowed: true }
        : { allowed: false, reason: 'Gates for GATHER not satisfied (e.g. participants with decision rights)' };
    case 'ARRANGE':
      return gateState.canEnterArrange
        ? { allowed: true }
        : { allowed: false, reason: 'Gates for ARRANGE not satisfied (e.g. items, criteria locked)' };
    case 'PRIORITIZE':
      return gateState.canEnterPrioritize
        ? { allowed: true }
        : { allowed: false, reason: 'Gates for PRIORITIZE not satisfied (e.g. pool locked, snapshot)' };
    default:
      return { allowed: false, reason: 'Unknown phase' };
  }
}

/**
 * Get currently active phases (in_progress status)
 */
export function getActivePhases(phaseStatuses: Record<DegapPhase, PhaseStatus>): DegapPhase[] {
  return PHASE_ORDER.filter(phase => phaseStatuses[phase].status === 'in_progress');
}

/**
 * Get the "primary" current phase (the rightmost active or the leftmost incomplete)
 * Used for backwards compatibility with single-phase UI
 */
export function getPrimaryPhase(phaseStatuses: Record<DegapPhase, PhaseStatus>): DegapPhase {
  const activePhases = getActivePhases(phaseStatuses);
  if (activePhases.length > 0) {
    // Return the rightmost active phase (most advanced)
    return activePhases[activePhases.length - 1];
  }
  
  // No active phases - return first incomplete phase
  for (const phase of PHASE_ORDER) {
    if (phaseStatuses[phase].status !== 'complete') {
      return phase;
    }
  }
  
  // All complete - return PRIORITIZE
  return 'PRIORITIZE';
}

// ============================================================================
// Cycle Entity
// ============================================================================

export type CycleStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

// ============================================================================
// Aggregation Configuration (Core B)
// ============================================================================

/**
 * Method for aggregating rankings from multiple sessions
 */
export type AggregationMethod = 
  | 'weighted_average'    // Average scores with session weights
  | 'median'              // Median score across sessions
  | 'borda_count'         // Borda count from ranks
  | 'rank_aggregation';   // Average ranks

/**
 * Tie-breaking strategy when items have equal aggregated scores
 */
export type TieBreaker = 
  | 'average_rank'        // Lower average rank wins
  | 'consensus_score'     // Higher consensus wins
  | 'recency';            // Most recent session wins

/**
 * Configuration for how session rankings are aggregated at cycle level
 * Cycle owner sets this configuration
 */
export interface AggregationConfig {
  method: AggregationMethod;
  
  // Weight per session (session ID → weight)
  // If not specified, all sessions are equally weighted
  sessionWeights?: Record<string, number>;
  
  // How to handle ties
  tieBreaker: TieBreaker;
  
  // Whether to preserve the calculated ranking separately from ratified
  preserveCalculated: boolean;
  
  // Minimum number of sessions required before aggregation
  minimumSessions?: number;
  
  // Whether to include incomplete sessions in aggregation
  includeIncomplete?: boolean;
}

/**
 * Default aggregation configuration
 */
export const DEFAULT_AGGREGATION_CONFIG: AggregationConfig = {
  method: 'weighted_average',
  tieBreaker: 'consensus_score',
  preserveCalculated: true,
  minimumSessions: 1,
  includeIncomplete: false,
};

/**
 * Result of aggregating multiple session rankings
 */
export interface AggregatedRanking {
  id: string;
  cycleId: string;
  
  // Source sessions that contributed
  sourceSessionIds: string[];
  
  // Configuration used
  config: AggregationConfig;
  
  // Aggregated results
  items: AggregatedRankingItem[];
  
  // Metadata
  aggregatedAt: Date;
  aggregatedBy: string;
  
  // Statistics
  statistics: AggregationStatistics;
}

/**
 * Single item in an aggregated ranking
 */
export interface AggregatedRankingItem {
  itemId: string;
  
  // Aggregated values
  aggregatedScore: number;
  aggregatedRank: number;
  
  // Per-session source data
  sourceScores: {
    sessionId: string;
    score: number;
    rank: number;
    weight: number;
  }[];
  
  // Variance metrics
  scoreVariance: number;
  rankVariance: number;
  
  // Consensus (0-1, higher = more agreement)
  consensusScore: number;
}

/**
 * Statistics about the aggregation
 */
export interface AggregationStatistics {
  sessionCount: number;
  itemCount: number;
  
  // Overall consensus metrics
  averageConsensus: number;
  minConsensus: number;
  maxConsensus: number;
  
  // Items with high disagreement
  highDisagreementItems: string[];
  
  // Items with perfect consensus
  perfectConsensusItems: string[];
}

// ============================================================================
// Strategy Reference (Upstream Boundary)
// ============================================================================

/**
 * StrategyRef is a lightweight reference to an upstream strategy.
 * We don't build strategy management - just acknowledge it exists.
 */
export interface StrategyRef {
  id?: string;                      // External strategy ID (if available)
  name: string;                     // Strategy name (e.g., "2026 Growth Strategy")
  description?: string;             // Brief description
  source?: 'internal' | 'external' | 'manual'; // Where this reference comes from
  /** Which kind of strategy `id` names, so the link opens the right page. Absent on older references, which are classic strategies. */
  kind?: 'classic' | 'ost' | 'kernel' | 'canvas' | 'v2mom';
  externalUrl?: string;             // Link to strategy document (Notion, Confluence, etc.)
  northStarGoal?: string;           // The key goal this cycle aligns to
}

export interface DegapCycle {
  id: string;
  name: string;
  description?: string;
  
  // Cycle mode and cadence (episodic, periodic, continuous)
  cadence: CycleCadence;
  
  // Phase tracking (legacy - for backwards compatibility)
  currentPhase: DegapPhase;
  phaseHistory: PhaseHistoryEntry[];
  
  // Phase tracking (Core B - parallel phase support)
  phaseStatuses: Record<DegapPhase, PhaseStatus>;
  activePhases: DegapPhase[];  // Multiple phases can be active simultaneously
  
  // Ownership (Core B - Cycle Owner decides aggregation)
  ownerId: string;
  
  // Aggregation configuration (Core B)
  aggregationConfig: AggregationConfig;
  
  // Aggregated ranking result (after running aggregation)
  aggregatedRanking?: AggregatedRanking;
  
  // Items in this cycle
  itemIds: string[];
  
  // Outputs per phase
  outputs: CycleOutputs;
  
  // Lifecycle
  status: CycleStatus;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  
  // Associated resources
  sessionIds: string[];
  itemPoolIds: string[];
  snapshotIds: string[];
  
  // === UPSTREAM BOUNDARY (Strategy) ===
  // Lightweight reference to the strategy this cycle supports
  strategyRef?: StrategyRef;

  /**
   * Owner ruling #9 (migration 767, degap_cycles.default_track_owner_id): the
   * person a track of this cycle is given as owner when it is created without
   * one (Launch, Generate tracks, the create-track form, the API).
   */
  defaultTrackOwnerId?: string | null;

  // === Workflow (Stage 1) ===
  /** Optional explicit Workflow for this cycle. NULL = use workspace default or canonical fallback. */
  workflowId?: string | null;

  /** Workspace this cycle belongs to (from DB). Used for effective workflow resolution. */
  workspaceId?: string | null;

  /** Readiness definition override for this cycle — null means inherit from workspace. */
  readinessDefinitionId?: string | null;

  // === CycleTemplate (applied once at creation) ===
  /**
   * The CycleTemplate that was applied at cycle creation (durable provenance).
   * Set once when the template is applied; never updated afterward.
   * Null if no template was applied or cycle predates templates.
   * Build Contract §2.1: must survive refresh — stored in degap_cycles.applied_template_id.
   */
  appliedTemplateId?: string | null;
  /** When the template seeded this cycle (mig 1040, server-set). Null = none applied. */
  templateAppliedAt?: string | Date | null;
  /**
   * The seed as it was taken (mig 1040, server-set). Tracks added later take
   * this, never the template's current contents.
   */
  templateSeed?: CycleTemplateSeed | null;

  // Metadata
  tags?: string[];
  metadata?: Record<string, unknown>;
  
  // ================================
  // Cycle Configuration Sessions
  // ================================
  
  /**
   * Configuration sessions for this cycle.
   * These manage pre-prioritization decisions (criteria, weights, tools, etc.)
   */
  configurationSessionIds?: string[];
  
  /**
   * ID of the currently active configuration session.
   * When locked, its decisions flow into prioritization sessions.
   */
  activeConfigSessionId?: string;
  
  // ================================
  // Cycle Prerequisites
  // ================================
  // Organizational Binding
  // ================================

  /**
   * The org unit node this cycle is anchored to.
   * Determines which users can see and participate in this cycle.
   * Visibility: users at or below this node in the org tree.
   */
  orgScopeNodeId?: string;

  /**
   * Binding to organizational hierarchy.
   * Controls which org units can submit items and who has permissions.
   */
  orgUnitBinding?: OrgUnitBinding;
  
  // ================================
  // Catch-Ball Process
  // ================================
  
  /**
   * ID of the Catchball game for this cycle.
   * The full CatchBallGame is stored in the catchball store.
   */
  catchBallGameId?: string;
  
  /**
   * Legacy: Catch-ball rounds for collaborative decision-making
   * @deprecated Use catchBallGameId and the catchball store instead
   */
  catchBallRounds?: CatchBallRound[];
  
  /**
   * Configuration for collaborative decisions during this cycle
   */
  collaborativeDecisionConfigs?: CollaborativeDecisionConfig[];

  // ================================
  // Decision Rights Configuration
  // ================================

  /**
   * Explicit decision rights for this cycle.
   *
   * Specifies, for each domain (participants, item_pool, criteria, scales, values, items)
   * and decision class (selection, range_selection, convergence, force_ranking):
   * - Who holds the right (owner)
   * - Which execution modes they may use (solo | delegate | session)
   * - Who may receive a delegated right (delegationConstraints)
   * - Who may reassign the right (metaAssignment — "who decides who decides")
   *
   * When absent, the Cycle Owner implicitly holds all rights with all permitted modes.
   * Use createDefaultDecisionRights() to materialize the defaults exclusively.
   *
   * See: src/types/prioritization/decisionRights.ts
   */
  decisionRightsConfig?: import('./decisionRights').DecisionRightsConfiguration;

  /**
   * UI experience mode for this cycle.
   * 'quick_exercise' — simplified single-page exercise workspace.
   * Absent / undefined — full DEGAP cycle UX.
   */
  uxMode?: 'quick_exercise' | string;
}

export type CycleRecord = DegapCycle;

// ============================================================================
// Catch-Ball Types (Legacy - see catchball.ts for full types)
// ============================================================================

/**
 * A catch-ball round in the legacy format
 * @deprecated Use CatchBallRound from catchball.ts instead
 */
export interface CatchBallRound {
  id: string;
  roundNumber: number;
  fromLevel: number;
  toLevel: number;
  direction: 'down' | 'up';
  status: 'pending' | 'in_progress' | 'complete';
  startedAt?: Date;
  completedAt?: Date;
}

/**
 * Configuration for collaborative decisions in a cycle
 */
export interface CollaborativeDecisionConfig {
  id: string;
  
  /** Type of decision being made collaboratively */
  decisionType: 'criteria_selection' | 'criteria_weighting' | 'tool_selection' | 'participant_selection';
  
  /** Who can participate in this decision */
  participantRoles: string[];
  
  /** Minimum number of participants required */
  minimumParticipants?: number;
  
  /** Whether unanimous agreement is required */
  requiresUnanimity: boolean;
  
  /** Deadline for the decision */
  dueDate?: Date;
  
  /** Current status */
  status: 'pending' | 'in_progress' | 'decided' | 'locked';
  
  /** The decision result (when decided) */
  result?: Record<string, unknown>;
  
  /** Who made the final decision */
  decidedBy?: string;
  
  /** When the decision was made */
  decidedAt?: Date;
}

/**
 * Binding between a cycle and the organizational hierarchy.
 * This controls scope for items and permissions.
 */
export interface OrgUnitBinding {
  /**
   * Primary org unit this cycle belongs to.
   * All permissions start from this scope.
   */
  primaryOrgUnitId: string;
  
  /**
   * Display name of the primary org unit (denormalized)
   */
  primaryOrgUnitName?: string;
  
  /**
   * Whether items can come from child org units.
   * If true, items from any descendant org unit are included.
   */
  includeChildOrgUnits: boolean;
  
  /**
   * Specific additional org units (if not using inheritance).
   * Useful for cross-functional cycles.
   */
  additionalOrgUnitIds?: string[];
  
  /**
   * Full path from root org unit for hierarchy resolution.
   * E.g., ["org-1", "dept-eng", "team-product"]
   */
  orgUnitPath?: string[];
  
  /**
   * Whether permissions cascade down to child org units.
   * If true, a permission at this level applies to all children.
   */
  permissionsCascade: boolean;
}

export interface PhaseHistoryEntry {
  phase: DegapPhase;
  enteredAt: Date;
  exitedAt?: Date;
  enteredBy: string;
  notes?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getNextPhase(current: DegapPhase): DegapPhase | null {
  const index = PHASE_ORDER.indexOf(current);
  if (index === -1 || index >= PHASE_ORDER.length - 1) return null;
  return PHASE_ORDER[index + 1];
}

export function getPreviousPhase(current: DegapPhase): DegapPhase | null {
  const index = PHASE_ORDER.indexOf(current);
  if (index <= 0) return null;
  return PHASE_ORDER[index - 1];
}

export function canTransitionTo(
  current: DegapPhase, 
  target: DegapPhase, 
  outputs: CycleOutputs
): { allowed: boolean; reason?: string; missing?: string[] } {
  const currentIndex = PHASE_ORDER.indexOf(current);
  const targetIndex = PHASE_ORDER.indexOf(target);
  
  // Can always go backward (for iteration)
  if (targetIndex < currentIndex) {
    return { allowed: true };
  }
  
  // Can only go forward one step at a time
  if (targetIndex > currentIndex + 1) {
    return { 
      allowed: false, 
      reason: `Cannot skip phases. Complete ${PHASE_ORDER[currentIndex + 1]} first.` 
    };
  }
  
  // Find transition requirements
  const transition = PHASE_TRANSITIONS.find(t => t.from === current && t.to === target);
  if (!transition) {
    return { allowed: true }; // No specific requirements
  }
  
  const result = transition.validate(outputs);
  if (!result.valid) {
    return { 
      allowed: false, 
      reason: 'Transition requirements not met',
      missing: result.missing,
    };
  }
  
  return { allowed: true };
}

export function createEmptyOutputs(): CycleOutputs {
  return {
    decide: {
      outcomeDrivers: [],
      roleAssignments: [],
    },
    engage: {
      participants: [],
      scheduledSessions: [],
    },
    gather: {
      shapedItemIds: [],
      itemCount: 0,
      readyForArrangeCount: 0,
    },
    arrange: {
      itemPoolIds: [],
      toolRuns: [],
      snapshotIds: [],
    },
    prioritize: {
      driftEvents: [],
      realignmentHistory: [],
    },
  };
}

export function getPhaseProgress(cycle: DegapCycle): Record<DegapPhase, number> {
  const outputs = cycle?.outputs;
  
  return {
    DECIDE: calculateDecideProgress(outputs?.decide),
    ENGAGE: calculateEngageProgress(outputs?.engage),
    GATHER: calculateGatherProgress(outputs?.gather),
    ARRANGE: calculateArrangeProgress(outputs?.arrange),
    PRIORITIZE: calculatePrioritizeProgress(outputs?.prioritize),
  };
}

function calculateDecideProgress(outputs: DecidePhaseOutputs | undefined): number {
  if (!outputs) return 0;
  let score = 0;
  if (outputs.outcomeDrivers?.length > 0) score += 40;
  if (outputs.roleAssignments?.length > 0) score += 30;
  if (outputs.clarityDocument) score += 15;
  if (outputs.domainBoundaries) score += 15;
  return Math.min(100, score);
}

function calculateEngageProgress(outputs: EngagePhaseOutputs | undefined): number {
  if (!outputs) return 0;
  let score = 0;
  if (outputs.participants?.length > 0) score += 50;
  if (outputs.scheduledSessions?.length > 0) score += 30;
  if (outputs.decisionRights) score += 20;
  return Math.min(100, score);
}

function calculateGatherProgress(outputs: GatherPhaseOutputs | undefined): number {
  if (!outputs || outputs.itemCount === 0) return 0;
  const readyRatio = (outputs.readyForArrangeCount || 0) / outputs.itemCount;
  return Math.round(readyRatio * 100);
}

function calculateArrangeProgress(outputs: ArrangePhaseOutputs | undefined): number {
  if (!outputs) return 0;
  let score = 0;
  if (outputs.itemPoolIds?.length > 0) score += 30;
  if (outputs.toolRuns?.length > 0) score += 40;
  if (outputs.snapshotIds?.length > 0) score += 30;
  return Math.min(100, score);
}

function calculatePrioritizeProgress(outputs: PrioritizePhaseOutputs | undefined): number {
  if (!outputs) return 0;
  if (outputs.publishedSnapshotId) return 100;
  if (outputs.cutLinePosition !== undefined) return 50;
  return 0;
}
